// =============================================================================
// components/ux/CommandPalette.tsx
// R2 Universal Search — Ctrl+K / Shift+Shift / Search Icon
// Searches BIM Objects, Tasks, Meetings, Annotations, Memory, Pages + Commands
// =============================================================================

import { useState, useEffect, useRef, useCallback, type JSX } from 'react';
import {
  Search, ArrowRight, Zap, LayoutDashboard, FolderOpen, FileText,
  Settings, Bot, Calendar, Shield, Activity, Sparkles, Clock,
  TrendingUp, ChevronRight, Command, Hash, CornerDownLeft,
  Box, ClipboardCheck, MessageSquare, BookOpen, StickyNote,
  Loader2, Building2, Database,
} from 'lucide-react';
import { useUXControlStore } from '../../stores/uxControl.store.js';

const API = import.meta.env.VITE_API_URL ?? (typeof process !== 'undefined' ? (process.env?.REACT_APP_BACKEND_URL ?? '') : '');

// ── Static Command Registry ───────────────────────────────────────────────────
export interface PaletteCommand {
  id: string;
  label: string;
  description?: string;
  group: string;
  keywords: string[];
  route?: string;
  action?: string;
  icon: JSX.Element;
  shortcut?: string;
  badge?: string;
}

const ALL_COMMANDS: PaletteCommand[] = [
  // Navigation
  { id: 'nav-dashboard',     label: 'Dashboard',               group: 'Navigate', keywords: ['home', 'overview', 'kpi'], route: 'dashboard',              icon: <LayoutDashboard size={14} /> },
  { id: 'nav-smartviewer',   label: 'SmartViewer 3D',          group: 'Navigate', keywords: ['3d', 'bim', 'viewer', 'twin'], route: 'r2-smart-viewer',   icon: <Box size={14} />, badge: 'BIM' },
  { id: 'nav-ctdte',         label: 'Commissioning & DT',      group: 'Navigate', keywords: ['commissioning', 'tasks', 'digital twin'], route: 'r2-ctdte', icon: <ClipboardCheck size={14} />, badge: 'BIM' },
  { id: 'nav-mie',           label: 'Meeting Intelligence',    group: 'Navigate', keywords: ['meeting', 'mie', 'rfi', 'decision'], route: 'r2-meeting-intelligence', icon: <MessageSquare size={14} />, badge: 'BIM' },
  { id: 'nav-annotations',   label: 'Smart Annotations',       group: 'Navigate', keywords: ['annotation', 'qa', 'punch', 'defect'], route: 'r2-annotations', icon: <StickyNote size={14} />, badge: 'BIM' },
  { id: 'nav-projects',      label: 'Projects',                group: 'Navigate', keywords: ['bim', 'project', 'rcdd'], route: 'projects',               icon: <FolderOpen size={14} /> },
  { id: 'nav-documents',     label: 'Documents',               group: 'Navigate', keywords: ['docs', 'files', 'pdf'],   route: 'documents',              icon: <FileText size={14} /> },
  { id: 'nav-calendar',      label: 'Project Calendar',        group: 'Navigate', keywords: ['calendar', 'schedule', 'meeting', 'event'], route: 'r2-calendar', icon: <Calendar size={14} />, badge: 'NEW' },
  { id: 'nav-safety',        label: 'Safety Intelligence',     group: 'Navigate', keywords: ['sos', 'lone', 'wellness', 'emergency'],     route: 'r2-safety',   icon: <Shield size={14} />,  badge: 'NEW' },
  { id: 'nav-automation',    label: 'Automation Studio',       group: 'Navigate', keywords: ['workflow', 'automation', 'ai'],             route: 'automation-studio', icon: <Zap size={14} />, badge: 'NEW' },
  { id: 'nav-finance',       label: 'Financial Command',       group: 'Navigate', keywords: ['stocks', 'market', 'finance'], route: 'r2-financial-command', icon: <TrendingUp size={14} />, badge: 'NEW' },
  { id: 'nav-ai',            label: 'AI Assistant',            group: 'Navigate', keywords: ['ai', 'chat', 'r2', 'assistant'],           route: 'ai',          icon: <Bot size={14} /> },
  { id: 'nav-health',        label: 'System Health',           group: 'Navigate', keywords: ['health', 'status', 'monitor'],             route: 'health',      icon: <Activity size={14} /> },
  { id: 'nav-settings',      label: 'Settings',                group: 'Navigate', keywords: ['settings', 'preferences', 'config'],       route: 'settings',    icon: <Settings size={14} />, shortcut: 'Ctrl+,' },
  { id: 'nav-mission',       label: 'Mission Control',         group: 'Navigate', keywords: ['mission', 'kpi', 'dashboard', 'executive'], route: 'r2-mission-control', icon: <Database size={14} /> },
  // R2 Platform
  { id: 'nav-arch',          label: 'AI Arch Engine',          group: 'R2 Platform', keywords: ['arch', 'ifc', 'ai', 'generate'],  route: 'r2-arch-engine',  icon: <Building2 size={14} /> },
  { id: 'nav-memory',        label: 'Living Memory',           group: 'R2 Platform', keywords: ['memory', 'knowledge'],             route: 'r2-memory-engine', icon: <BookOpen size={14} /> },
  { id: 'nav-agent',         label: 'Agent Projects',          group: 'R2 Platform', keywords: ['agent', 'platform'],               route: 'agent',           icon: <Bot size={14} /> },
  { id: 'nav-clash',         label: 'Clash Detection',         group: 'R2 Platform', keywords: ['clash', 'conflict'],               route: 'clashes',         icon: <Activity size={14} /> },
  { id: 'nav-assets',        label: 'Asset Registry',          group: 'R2 Platform', keywords: ['assets', 'equipment'],             route: 'assets',          icon: <FolderOpen size={14} /> },
  { id: 'nav-global-command', label: 'Global Command',         group: 'R2 Platform', keywords: ['command', 'global'],               route: 'global-command',  icon: <Command size={14} /> },
  // Actions
  { id: 'action-screenshot', label: 'Take Screenshot',         group: 'Actions', keywords: ['screenshot', 'capture', 'print'],   action: 'screenshot',       icon: <Hash size={14} /> },
  { id: 'action-night-ops',  label: 'Toggle Night Ops Mode',   group: 'Actions', keywords: ['night', 'dark', 'ops'],              action: 'toggle-night-ops', icon: <Hash size={14} /> },
];

// ── Type icons ────────────────────────────────────────────────────────────────
const TYPE_ICONS: Record<string, JSX.Element> = {
  bim_object:  <Box size={13} style={{ color: '#5eead4' }} />,
  task:        <ClipboardCheck size={13} style={{ color: '#60a5fa' }} />,
  meeting:     <MessageSquare size={13} style={{ color: '#a78bfa' }} />,
  annotation:  <StickyNote size={13} style={{ color: '#f59e0b' }} />,
  memory:      <BookOpen size={13} style={{ color: '#34d399' }} />,
  page:        <ArrowRight size={13} style={{ color: '#94a3b8' }} />,
};

const TYPE_LABELS: Record<string, string> = {
  bim_object:  'BIM Objects',
  task:        'Tasks',
  meeting:     'Meetings',
  annotation:  'Annotations',
  memory:      'Knowledge',
  page:        'Pages',
};

const STATUS_COLORS: Record<string, string> = {
  open:        '#f87171',
  in_progress: '#fbbf24',
  complete:    '#4ade80',
  closed:      '#6b7280',
  critical:    '#f87171',
  warning:     '#fbbf24',
  info:        '#60a5fa',
};

// ── Fuzzy match ───────────────────────────────────────────────────────────────
function fuzzyMatch(query: string, cmd: PaletteCommand): boolean {
  const q = query.toLowerCase();
  return (
    cmd.label.toLowerCase().includes(q) ||
    !!cmd.description?.toLowerCase().includes(q) ||
    cmd.keywords.some((k) => k.includes(q)) ||
    cmd.group.toLowerCase().includes(q)
  );
}

function highlight(text: string, query: string): JSX.Element {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: 'rgba(94,234,212,0.25)', color: '#5eead4', borderRadius: 2 }}>
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// ── Universal Search Result Type ──────────────────────────────────────────────
interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle: string;
  meta: Record<string, unknown>;
  action: string;
  action_payload: { route?: string; object_id?: string };
  score: number;
}

interface SearchGroups {
  bim_objects:  SearchResult[];
  tasks:        SearchResult[];
  meetings:     SearchResult[];
  annotations:  SearchResult[];
  memory:       SearchResult[];
  pages:        SearchResult[];
}

// ── Component ─────────────────────────────────────────────────────────────────
interface CommandPaletteProps {
  onNavigate: (route: string) => void;
  onAction?: (action: string) => void;
}

export function CommandPalette({ onNavigate, onAction }: CommandPaletteProps): JSX.Element | null {
  const { commandPaletteOpen, closeCommandPalette, usageRecords, getAdaptiveSuggestions } = useUXControlStore();
  const [query, setQuery]         = useState('');
  const [selected, setSelected]   = useState(0);
  const [apiResults, setApiResults] = useState<SearchGroups | null>(null);
  const [loading, setLoading]     = useState(false);
  const inputRef   = useRef<HTMLInputElement>(null);
  const listRef    = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Adaptive recent suggestions
  const suggestions     = getAdaptiveSuggestions();
  const recentCommands  = ALL_COMMANDS.filter((c) => c.route && suggestions.includes(c.route)).slice(0, 3);

  // Filter static commands
  const cmdResults = query
    ? ALL_COMMANDS.filter((c) => fuzzyMatch(query, c))
    : ALL_COMMANDS.slice(0, 10);

  // ── Fetch live API search ────────────────────────────────────────────────────
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) { setApiResults(null); setLoading(false); return; }

    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/api/r2-search/universal?q=${encodeURIComponent(query)}`);
        if (res.ok) {
          const data = await res.json();
          setApiResults(data.groups);
        }
      } catch { /* silently fail */ }
      finally { setLoading(false); }
    }, 200);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // ── Build flat list for keyboard nav ─────────────────────────────────────────
  interface FlatItem {
    key: string;
    isSearchResult: boolean;
    result?: SearchResult;
    cmd?: PaletteCommand;
  }

  const flatItems: FlatItem[] = [];

  if (apiResults && query.length >= 2) {
    // BIM objects first (most valuable)
    (apiResults.bim_objects ?? []).forEach((r) => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
    (apiResults.tasks ?? []).forEach((r)       => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
    (apiResults.meetings ?? []).forEach((r)    => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
    (apiResults.annotations ?? []).forEach((r) => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
    (apiResults.memory ?? []).forEach((r)      => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
    (apiResults.pages ?? []).forEach((r)       => flatItems.push({ key: r.id, isSearchResult: true, result: r }));
  }

  // Always append matching static commands
  cmdResults.forEach((c) => flatItems.push({ key: c.id, isSearchResult: false, cmd: c }));

  // ── Execute selection ─────────────────────────────────────────────────────────
  const execute = useCallback((item: FlatItem) => {
    closeCommandPalette();
    setQuery('');
    setApiResults(null);
    if (item.isSearchResult && item.result) {
      const r = item.result;
      if (r.action === 'navigate_to_viewer') {
        onNavigate('r2-smart-viewer');
        // Dispatch custom event so SmartViewer can auto-select the object
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('r2:viewer:navigate-to-object', {
            detail: { objectId: r.action_payload.object_id },
          }));
        }, 300);
      } else if (r.action_payload?.route) {
        onNavigate(r.action_payload.route);
      }
    } else if (item.cmd) {
      if (item.cmd.route) { onNavigate(item.cmd.route); return; }
      if (item.cmd.action) { onAction?.(item.cmd.action); }
    }
  }, [closeCommandPalette, onNavigate, onAction]);

  // ── Focus + reset on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (commandPaletteOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      setQuery('');
      setSelected(0);
      setApiResults(null);
    }
  }, [commandPaletteOpen]);

  // ── Keyboard navigation ───────────────────────────────────────────────────────
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (!commandPaletteOpen) return;
      if (e.key === 'Escape') { closeCommandPalette(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelected((s) => Math.min(s + 1, flatItems.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[selected];
        if (item) execute(item);
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [commandPaletteOpen, flatItems, selected, execute, closeCommandPalette]);

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-cmd-idx="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!commandPaletteOpen) return null;

  // Check if there are any API results
  const hasApiResults = apiResults && (
    (apiResults.bim_objects?.length ?? 0) +
    (apiResults.tasks?.length ?? 0) +
    (apiResults.meetings?.length ?? 0) +
    (apiResults.annotations?.length ?? 0) +
    (apiResults.memory?.length ?? 0) +
    (apiResults.pages?.length ?? 0)
  ) > 0;

  let itemIdx = 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeCommandPalette}
        style={{
          position: 'fixed', inset: 0, zIndex: 99800,
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)',
        }}
      />

      {/* Palette panel */}
      <div
        data-testid="command-palette"
        style={{
          position: 'fixed', top: '10%', left: '50%', transform: 'translateX(-50%)',
          zIndex: 99900, width: 680, maxWidth: '94vw',
          background: 'rgba(3,8,22,0.98)',
          border: '1px solid rgba(37,181,168,0.28)',
          borderRadius: 18,
          boxShadow: '0 32px 100px rgba(0,0,0,0.8), 0 0 0 1px rgba(37,181,168,0.1)',
          overflow: 'hidden',
          animation: 'cmdPaletteIn 0.18s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {/* Top accent bar */}
        <div style={{ height: 2, background: 'linear-gradient(90deg, #25B5A8, #818cf8, #f472b6, transparent)' }} />

        {/* Search input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid rgba(37,181,168,0.1)' }}>
          {loading
            ? <Loader2 size={16} style={{ color: '#5eead4', flexShrink: 0, animation: 'spin 0.8s linear infinite' }} />
            : <Search size={16} style={{ color: 'rgba(37,181,168,0.7)', flexShrink: 0 }} />
          }
          <input
            ref={inputRef}
            type="text"
            placeholder="Search BIM objects, tasks, meetings, pages…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              color: '#f0f4ff', fontSize: 15, fontWeight: 500,
            }}
            data-testid="command-palette-input"
          />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <kbd style={{
              fontSize: 9, padding: '2px 6px', borderRadius: 4,
              background: 'rgba(37,181,168,0.1)', border: '1px solid rgba(37,181,168,0.25)',
              color: 'rgba(94,234,212,0.6)', fontFamily: 'monospace', fontWeight: 700,
            }}>Ctrl+K</kbd>
            <kbd style={{
              fontSize: 10, padding: '2px 6px', borderRadius: 4,
              background: 'rgba(30,50,80,0.5)', border: '1px solid rgba(50,70,100,0.4)',
              color: 'rgba(148,172,210,0.35)', fontFamily: 'monospace',
            }}>ESC</kbd>
          </div>
        </div>

        {/* Adaptive suggestions (shown when empty) */}
        {!query && recentCommands.length > 0 && (
          <div style={{ padding: '8px 16px 4px', borderBottom: '1px solid rgba(37,181,168,0.07)' }}>
            <div style={{ fontSize: 9, color: 'rgba(37,181,168,0.4)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 6 }}>
              R2 Suggests
            </div>
            <div style={{ display: 'flex', gap: 6, paddingBottom: 8, flexWrap: 'wrap' }}>
              {recentCommands.map((cmd) => (
                <button
                  key={cmd.id}
                  onClick={() => execute({ key: cmd.id, isSearchResult: false, cmd })}
                  style={{
                    padding: '4px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11,
                    background: 'rgba(37,181,168,0.08)', border: '1px solid rgba(37,181,168,0.18)',
                    color: '#5eead4', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5,
                  }}
                >
                  {cmd.icon} {cmd.label}
                </button>
              ))}
              {/* BIM shortcut chips */}
              <button
                onClick={() => { closeCommandPalette(); onNavigate('r2-smart-viewer'); }}
                style={{ padding: '4px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11, background: 'rgba(94,234,212,0.06)', border: '1px solid rgba(94,234,212,0.18)', color: '#5eead4', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <Box size={10} /> SmartViewer 3D
              </button>
              <button
                onClick={() => { closeCommandPalette(); onNavigate('r2-ctdte'); }}
                style={{ padding: '4px 10px', borderRadius: 7, cursor: 'pointer', fontSize: 11, background: 'rgba(96,165,250,0.06)', border: '1px solid rgba(96,165,250,0.18)', color: '#60a5fa', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
              >
                <ClipboardCheck size={10} /> Commissioning
              </button>
            </div>
          </div>
        )}

        {/* Results list */}
        <div ref={listRef} style={{ maxHeight: 460, overflowY: 'auto', padding: '4px 0 8px' }}>
          {/* ── Live API Search Results ── */}
          {query.length >= 2 && !loading && !hasApiResults && cmdResults.length === 0 && (
            <div style={{ padding: '24px', textAlign: 'center', color: 'rgba(100,140,180,0.4)', fontSize: 13 }}>
              No results for "{query}"
            </div>
          )}

          {query.length >= 2 && hasApiResults && apiResults && (
            <>
              {/* BIM Objects section */}
              {(apiResults.bim_objects?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="BIM Objects" icon={<Box size={9} />} />
                  {apiResults.bim_objects.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Tasks section */}
              {(apiResults.tasks?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="Tasks & Commissioning" icon={<ClipboardCheck size={9} />} />
                  {apiResults.tasks.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Meetings section */}
              {(apiResults.meetings?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="Meetings" icon={<MessageSquare size={9} />} />
                  {apiResults.meetings.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Annotations section */}
              {(apiResults.annotations?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="Annotations" icon={<StickyNote size={9} />} />
                  {apiResults.annotations.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Memory section */}
              {(apiResults.memory?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="Knowledge Base" icon={<BookOpen size={9} />} />
                  {apiResults.memory.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}

              {/* Pages section */}
              {(apiResults.pages?.length ?? 0) > 0 && (
                <div style={{ marginBottom: 2 }}>
                  <SectionLabel label="Pages" icon={<ArrowRight size={9} />} />
                  {apiResults.pages.map((r) => {
                    const idx = itemIdx++;
                    return (
                      <SearchResultRow
                        key={r.id} result={r} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: r.id, isSearchResult: true, result: r })}
                      />
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── Static Platform Commands ── */}
          {(cmdResults.length > 0) && (
            <div style={{ marginBottom: 2 }}>
              {(query.length >= 2) && <SectionLabel label="Platform Commands" icon={<Command size={9} />} />}

              {/* Grouped commands when empty query */}
              {!query ? (
                Object.entries(
                  ALL_COMMANDS.slice(0, 12).reduce<Record<string, PaletteCommand[]>>((acc, cmd) => {
                    (acc[cmd.group] ??= []).push(cmd);
                    return acc;
                  }, {})
                ).map(([group, cmds]) => (
                  <div key={group} style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 9, color: 'rgba(100,140,180,0.3)', textTransform: 'uppercase', letterSpacing: '0.14em', padding: '6px 20px 3px', fontWeight: 700 }}>
                      {group}
                    </div>
                    {cmds.map((cmd) => {
                      const idx = itemIdx++;
                      return (
                        <CommandRow
                          key={cmd.id} cmd={cmd} query={query}
                          selected={idx === selected} idx={idx}
                          onSelect={() => execute({ key: cmd.id, isSearchResult: false, cmd })}
                        />
                      );
                    })}
                  </div>
                ))
              ) : (
                <div style={{ padding: '0 8px' }}>
                  {cmdResults.map((cmd) => {
                    const idx = itemIdx++;
                    return (
                      <CommandRow
                        key={cmd.id} cmd={cmd} query={query}
                        selected={idx === selected} idx={idx}
                        onSelect={() => execute({ key: cmd.id, isSearchResult: false, cmd })}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
          borderTop: '1px solid rgba(37,181,168,0.08)',
          background: 'rgba(3,6,18,0.6)',
        }}>
          <span style={{ fontSize: 10, color: 'rgba(100,140,180,0.35)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <CornerDownLeft size={10} /> select
          </span>
          <span style={{ fontSize: 10, color: 'rgba(100,140,180,0.35)' }}>↑↓ navigate</span>
          <span style={{ fontSize: 10, color: 'rgba(100,140,180,0.35)' }}>ESC close</span>
          <span style={{ marginLeft: 'auto', fontSize: 9, color: 'rgba(37,181,168,0.3)', fontFamily: 'monospace', letterSpacing: '0.08em' }}>
            R2 UNIVERSAL SEARCH
          </span>
        </div>
      </div>

      <style>{`
        @keyframes cmdPaletteIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-14px) scale(0.96); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        input::placeholder { color: rgba(100,140,180,0.35); }
      `}</style>
    </>
  );
}

// ── Section Label ─────────────────────────────────────────────────────────────
function SectionLabel({ label, icon }: { label: string; icon: JSX.Element }): JSX.Element {
  return (
    <div style={{
      fontSize: 9, color: 'rgba(100,140,180,0.4)', textTransform: 'uppercase',
      letterSpacing: '0.14em', padding: '6px 20px 3px', fontWeight: 700,
      display: 'flex', alignItems: 'center', gap: 5,
    }}>
      {icon} {label}
    </div>
  );
}

// ── Search Result Row ─────────────────────────────────────────────────────────
function SearchResultRow({ result, query, selected, idx, onSelect }: {
  result: SearchResult; query: string; selected: boolean; idx: number; onSelect: () => void;
}): JSX.Element {
  const icon = TYPE_ICONS[result.type] ?? <ArrowRight size={13} />;
  const isBim = result.type === 'bim_object';
  const status = result.meta?.status as string | undefined;
  const statusColor = status ? STATUS_COLORS[status] ?? '#6b7280' : undefined;
  const discipline = result.meta?.discipline as string | undefined;
  const priority = result.meta?.priority as string | undefined;

  return (
    <button
      type="button"
      data-cmd-idx={idx}
      data-testid={`search-result-${result.id}`}
      onClick={onSelect}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
        background: selected ? 'rgba(37,181,168,0.08)' : 'none',
        border: `1px solid ${selected ? 'rgba(37,181,168,0.2)' : 'transparent'}`,
        textAlign: 'left', transition: 'background 0.08s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.06)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = selected ? 'rgba(37,181,168,0.08)' : 'none'; }}
    >
      {/* Type icon */}
      <span style={{
        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: isBim ? 'rgba(94,234,212,0.08)' : 'rgba(30,50,80,0.5)',
        border: `1px solid ${isBim ? 'rgba(94,234,212,0.15)' : 'rgba(50,70,100,0.3)'}`,
      }}>
        {icon}
      </span>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#f0f4ff' : '#c8d8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {highlight(result.title, query)}
        </div>
        <div style={{ fontSize: 11, color: 'rgba(100,140,180,0.5)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {highlight(result.subtitle, query)}
        </div>
      </div>

      {/* Badges */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
        {discipline && (
          <span style={{
            fontSize: 8, padding: '2px 5px', borderRadius: 4,
            background: 'rgba(94,234,212,0.08)', border: '1px solid rgba(94,234,212,0.2)',
            color: '#5eead4', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{discipline.slice(0, 5)}</span>
        )}
        {status && (
          <span style={{
            fontSize: 8, padding: '2px 5px', borderRadius: 4,
            background: `${statusColor}18`, border: `1px solid ${statusColor}40`,
            color: statusColor, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{status}</span>
        )}
        {priority && priority !== 'medium' && (
          <span style={{
            fontSize: 8, padding: '2px 5px', borderRadius: 4,
            background: priority === 'critical' ? 'rgba(248,113,113,0.1)' : 'rgba(251,191,36,0.1)',
            border: `1px solid ${priority === 'critical' ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.3)'}`,
            color: priority === 'critical' ? '#f87171' : '#fbbf24', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{priority}</span>
        )}
        {isBim && (
          <span style={{
            fontSize: 9, color: '#5eead4', opacity: selected ? 1 : 0.5,
            display: 'flex', alignItems: 'center', gap: 3, fontWeight: 600,
          }}>
            <Box size={9} /> Fly to
          </span>
        )}
        {selected && <ChevronRight size={12} style={{ color: '#5eead4' }} />}
      </div>
    </button>
  );
}

// ── Command Row (static commands) ─────────────────────────────────────────────
function CommandRow({ cmd, query, selected, idx, onSelect }: {
  cmd: PaletteCommand; query: string; selected: boolean; idx: number; onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-cmd-idx={idx}
      data-testid={`palette-cmd-${cmd.id}`}
      onClick={onSelect}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
        background: selected ? 'rgba(37,181,168,0.1)' : 'none',
        border: `1px solid ${selected ? 'rgba(37,181,168,0.22)' : 'transparent'}`,
        textAlign: 'left', transition: 'background 0.08s',
      }}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(37,181,168,0.05)'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = selected ? 'rgba(37,181,168,0.1)' : 'none'; }}
    >
      <span style={{ color: selected ? '#5eead4' : 'rgba(100,140,180,0.45)', flexShrink: 0 }}>{cmd.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: selected ? '#f0f4ff' : '#c8d8f0' }}>
          {highlight(cmd.label, query)}
        </div>
        {cmd.description && (
          <div style={{ fontSize: 11, color: 'rgba(100,140,180,0.4)', marginTop: 1 }}>
            {highlight(cmd.description, query)}
          </div>
        )}
      </div>
      {cmd.badge && (
        <span style={{
          fontSize: 8, padding: '2px 5px', borderRadius: 4, flexShrink: 0,
          background: 'rgba(94,234,212,0.1)', border: '1px solid rgba(94,234,212,0.22)',
          color: '#5eead4', fontWeight: 700, letterSpacing: '0.08em',
        }}>{cmd.badge}</span>
      )}
      {cmd.shortcut && (
        <kbd style={{
          fontSize: 9, padding: '1px 5px', borderRadius: 4, flexShrink: 0,
          background: 'rgba(30,50,80,0.6)', border: '1px solid rgba(50,70,100,0.4)',
          color: 'rgba(148,172,210,0.4)', fontFamily: 'monospace',
        }}>{cmd.shortcut}</kbd>
      )}
      {selected && <ChevronRight size={12} style={{ color: '#5eead4', flexShrink: 0 }} />}
    </button>
  );
}
